// src/components/FinancialIntelligence/FIInputBar.js
//
// Bottom input bar for Financial Intelligence.
// Mirrors the QuestionForm SSE streaming pattern exactly:
// - getIdToken() → fetch → SSE reader loop → callbacks
// - No pre-created Firestore doc (backend creates it in Step 13)
// - On 'done' event receives entryId + followUpSuggestions

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { toast } from 'react-toastify';

const FI_STREAMING_ENDPOINT = `https://us-central1-${getApp().options.projectId}.cloudfunctions.net/generateFinancialIntelligenceStream`;

const FIInputBar = ({
  clientId,
  client,
  clientIsActive,
  clients,
  user,
  value,
  onChange,
  situationKey,
  isStreaming,
  onSubmitComplete,
  onStreamStart,
  onStreamChunk,
  onStreamPhase,
  onStreamDone,
  onStreamError,
  onToggleFilter,
  filterOpen,
}) => {
  const abortRef       = useRef(null);
  const inputRef       = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Abort on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Focus input when situation chip pre-fills
  useEffect(() => {
    if (value && inputRef.current) {
      inputRef.current.focus();
    }
  }, [value]);

  const isDisabled = !clientId || clientId === 'ALL' || !clientIsActive || isStreaming || isSubmitting;
  const canSubmit  = !!clientId && clientId !== 'ALL' && clientIsActive && !isStreaming && !isSubmitting && value.trim().length > 0;

  const getPlaceholder = () => {
    if (!clientId || clientId === 'ALL') return 'Select a client to begin…';
    if (!clientIsActive) return 'This client is inactive…';
    if (client) return `What do you want to understand about ${client.displayName || client.name}?`;
    return 'What do you want to understand about this client?';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

        const handleSubmit = async () => {
    if (!canSubmit || isStreaming) return;

    setIsSubmitting(true); // Immediate local feedback before any async work

    const rawPrompt  = value.trim();
    const triggerType = (situationKey && situationKey !== 'freeform')
      ? 'situation_starter'
      : 'freeform_prompt';

    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      toast.error('Not authenticated. Please refresh and try again.');
      setIsSubmitting(false);
      return;
    }

    onStreamStart();
    onSubmitComplete(); // Clear input immediately

    try {
      const idToken = await currentUser.getIdToken();
      const abortController = new AbortController();
      abortRef.current = abortController;

      const response = await fetch(FI_STREAMING_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          clientId,
          rawPrompt,
          triggerType,
          situationKey: situationKey || 'freeform',
        }),
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Streaming HTTP ${response.status}`);
      }

      // Hand off visual loading state once the streaming connection is live
      setIsSubmitting(false);

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';
      let sseComplete = false;

      while (!sseComplete) {
        const { done, value: chunk } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(chunk, { stream: true });
        const events = sseBuffer.split('\n\n');
        sseBuffer = events.pop() || '';

        for (const rawEvent of events) {
          const lines = rawEvent
            .split('\n')
            .map(l => l.trimEnd())
            .filter(Boolean);

          const dataLine = lines.find(l => l.startsWith('data: '));
          if (!dataLine) continue;

          try {
            const event = JSON.parse(dataLine.slice(6));

            if (event.type === 'status' && onStreamPhase) {
              onStreamPhase(event.phase);
            }

            if (event.type === 'chunk' && onStreamChunk) {
              onStreamChunk(event.text || '');
            }

            if (event.type === 'error') {
              toast.error(event.message || 'Analysis failed. Please try again.');
              setIsSubmitting(false);
              onStreamError();
              sseComplete = true;
              break;
            }

            if (event.type === 'done') {
              setIsSubmitting(false);
              onStreamDone(event.entryId, event.followUpSuggestions || []);
              sseComplete = true;
              break;
            }
          } catch {
            // Ignore malformed SSE payloads
          }
        }
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        setIsSubmitting(false);
        return;
      }
      console.error('[FIInputBar] Streaming error:', err);
      toast.error('Analysis failed. Please try again.');
      setIsSubmitting(false);
      onStreamError();
    }
  };

    return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <IconButton
          onClick={onToggleFilter}
          disabled={isStreaming || !clientId || clientId === 'ALL'}
          size="small"
          sx={{
            mb: 0.4,
            color: filterOpen ? 'primary.main' : 'text.secondary',
            bgcolor: filterOpen ? 'primary.50' : 'transparent',
            border: '1px solid',
            borderColor: filterOpen ? 'primary.main' : 'divider',
            width: 36,
            height: 36,
            '&:hover': {
              bgcolor: filterOpen ? 'primary.100' : 'grey.100',
            },
            '&.Mui-disabled': {
              color: 'text.disabled',
              borderColor: 'divider',
            },
          }}
        >
          <FilterListIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <TextField
          inputRef={inputRef}
          multiline
          minRows={1}
          maxRows={6}
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          disabled={isDisabled}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
              bgcolor: 'grey.50',
              pr: 1,
              '& fieldset': { borderColor: 'grey.300' },
              '&:hover fieldset': { borderColor: 'grey.400' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
            },
            '& .MuiInputBase-input': {
              fontSize: '0.95rem',
              py: 1.2,
              '&::placeholder': { opacity: 0.5 },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ ml: 0.5, alignSelf: 'flex-end', mb: 0.5 }}>
                                <IconButton
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  size="small"
                  sx={{
                    bgcolor: canSubmit ? 'primary.main' : 'grey.200',
                    color: canSubmit ? 'white' : 'text.disabled',
                    width: 34,
                    height: 34,
                    '&:hover': { bgcolor: canSubmit ? 'primary.dark' : 'grey.200' },
                    '&.Mui-disabled': { bgcolor: 'grey.200', color: 'text.disabled' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {isSubmitting
                    ? <CircularProgress size={16} sx={{ color: 'white' }} />
                    : <SendIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{ maxLength: 3000 }}
        />
      </Box>

      {/* Character count */}
      {value.length > 0 && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', textAlign: 'right', mt: 0.3, mr: 1, fontSize: '0.7rem' }}
        >
          {value.length}/3000
        </Typography>
      )}
    </Box>
  );
};

export default FIInputBar;